import { MonitorPlay } from 'lucide-react'
import { GRACKLE_COLORS } from '@/lib/theme'
import { WorksheetScreen } from '@/components/training/WorksheetScreen'
import type { SheetColumn } from '@/components/training/SheetTable'
import type { TrainingWorksheetRow, TrainingVocabularies } from '@/types'

/**
 * The "Advanced GPDF Demo" tab — a demo meeting log.
 *
 * "Meeting date" is text, not a date. The source has it hand-typed and inconsistent
 * — "April 22nd 2025", "May 1st, 2025" — so parsing it would either guess or throw
 * away rows. Kept verbatim; the header says so.
 *
 * Three of its columns are dropdowns in the sheet and were free text here until
 * migration 126: the rep list, the meeting length, and the status.
 */

// The three the sheet offers, no more. "Booked", "No show" and "Cancelled" were
// invented here and no row has ever held one.
const STATUS_TINTS: Record<string, string> = {
  Completed: GRACKLE_COLORS.pistachio,
  'In progress': GRACKLE_COLORS.pollen,
  'Not started': GRACKLE_COLORS.powderBlue,
}

const columns = (vocab?: TrainingVocabularies): SheetColumn[] => [
  { key: 'account_name', label: 'CRM account', kind: 'readonly', width: 'min-w-[15rem]' },
  { key: 'client_name', label: 'Client', kind: 'text', width: 'min-w-[15rem]' },
  {
    key: 'meeting_date_text',
    label: 'Meeting date',
    kind: 'text',
    width: 'min-w-[12rem]',
    note: 'free text in the sheet',
  },
  {
    // The sheet's header says "Meeting minutes" but the column is a meeting LENGTH —
    // its validation offers 30, 45 and 60, and every stored value is one of those.
    // Previously rendered as a paragraph box. The header text is kept so the two can
    // still be read side by side, with the qualification made explicit.
    key: 'meeting_minutes',
    label: 'Meeting minutes',
    kind: 'select',
    options: vocab?.demo_duration_minutes ?? [],
    width: 'min-w-[9rem]',
    note: 'meeting length',
  },
  {
    key: 'received_form',
    label: 'Received form',
    kind: 'select',
    options: vocab?.yes_no ?? [],
    width: 'min-w-[9rem]',
  },
  {
    // First names, as the sheet has them.
    key: 'sales_rep_name',
    label: 'Sales rep',
    kind: 'select',
    options: vocab?.demo_sales_rep ?? [],
    width: 'min-w-[11rem]',
  },
  { key: 'trainer_name', label: 'Trainer', kind: 'text', width: 'min-w-[11rem]' },
  {
    key: 'status',
    label: 'Status',
    kind: 'select',
    options: vocab?.demo_status ?? [],
    width: 'min-w-[10rem]',
  },
  { key: 'notes', label: 'Notes', kind: 'longtext', width: 'min-w-[20rem]' },
]

export default function TrainingDemos() {
  return (
    <WorksheetScreen
      sheet="demos"
      title="Advanced Demo"
      description="The Advanced GPDF Demo log. Meeting dates are kept as they were typed in the sheet."
      icon={<MonitorPlay className="h-6 w-6" aria-hidden="true" />}
      columns={columns}
      tints={STATUS_TINTS}
      tintFor={(r: TrainingWorksheetRow) => STATUS_TINTS[String(r.status ?? '')]}
      rowLabel={(r) => String(r.client_name ?? r.account_name ?? 'this client')}
      caption="Advanced GracklePDF demo log by client. Rows are tinted by status; the colour key is below the table."
      searchPlaceholder="Search client, minutes or notes..."
    />
  )
}
